import { css } from 'aphrodite';
import * as React from 'react';
import { ISupportTicket } from '../../../../models/Support/ISupportTicket';
import { Button } from '../../../../web/components-v2/Button';
import { ComposerRecipientInput } from './ComposerRecipientInput';
import { composerStyles as styles } from './composerStyles';
import type { ComposerReplyType } from './types';
import { parseEmails } from './useComposerSend';

interface ComposerAddressFieldsProps {
	replyType: ComposerReplyType;
	ticket: ISupportTicket;
	replyToValue: string;
	setReplyToValue: React.Dispatch<React.SetStateAction<string>>;
	forwardToValue: string;
	setForwardToValue: React.Dispatch<React.SetStateAction<string>>;
	ccValue: string;
	setCcValue: React.Dispatch<React.SetStateAction<string>>;
	bccValue: string;
	setBccValue: React.Dispatch<React.SetStateAction<string>>;
	showCc: boolean;
	setShowCc: React.Dispatch<React.SetStateAction<boolean>>;
	showBcc: boolean;
	setShowBcc: React.Dispatch<React.SetStateAction<boolean>>;
}

/** To / Cc / Bcc hairline rows for the composer. Notes never send email, so nothing renders there. */
export const ComposerAddressFields: React.FC<ComposerAddressFieldsProps> = ({
	replyType,
	ticket,
	replyToValue,
	setReplyToValue,
	forwardToValue,
	setForwardToValue,
	ccValue,
	setCcValue,
	bccValue,
	setBccValue,
	showCc,
	setShowCc,
	showBcc,
	setShowBcc,
}) => {
	if (replyType === 'note') return null;

	const isForward = replyType === 'forward';
	const csm = ticket.csm?.email?.trim();
	const ccEmails = parseEmails(ccValue).map(value => value.toLowerCase());
	const showAddCsm = !isForward && !!csm && !ccEmails.includes(csm.toLowerCase());
	const csmLabel = ticket.csm?.firstName ? `+ cc ${ticket.csm.firstName} (CSM)` : '+ cc CSM';

	const toEmails = parseEmails(isForward ? forwardToValue : replyToValue);
	const usedEmails = [...toEmails, ...ccEmails, ...parseEmails(bccValue)];
	const suggestionProps = {
		accountName: ticket.accountName,
		excludedEmails: usedEmails,
		ticketId: ticket.id,
	};
	// Reply and forward hold separate address sets, so a mode switch swaps every field's value. Keyed by
	// mode, the fields remount with it — sharing a fiber would carry one draft's caret into the other's text.
	const fieldKey = isForward ? 'forward' : 'conversation';

	return (
		<div className={css(styles.addressFields)}>
			<div className={css(styles.addressRow)}>
				<span className={css(styles.addressLabel)}>To</span>
				{isForward ? (
					<ComposerRecipientInput
						{...suggestionProps}
						key={`${fieldKey}-to`}
						placeholder='Enter recipient emails, comma-separated'
						ariaLabel='Forward recipients'
						value={forwardToValue}
						onChange={setForwardToValue}
						autoFocus
					/>
				) : (
					<ComposerRecipientInput
						{...suggestionProps}
						key={`${fieldKey}-to`}
						placeholder='Recipient email'
						ariaLabel='Reply recipient'
						value={replyToValue}
						onChange={setReplyToValue}
					/>
				)}
				{!showCc && (
					<Button variant='ghost' size='sm' styles={[styles.addressToggleBtn]} onClick={() => setShowCc(true)}>
						cc
					</Button>
				)}
				{!showBcc && (
					<Button variant='ghost' size='sm' styles={[styles.addressToggleBtn]} onClick={() => setShowBcc(true)}>
						bcc
					</Button>
				)}
			</div>
			{showCc && (
				<div className={css(styles.addressSubRow)}>
					<span className={css(styles.addressLabel)}>Cc</span>
					<ComposerRecipientInput
						{...suggestionProps}
						key={`${fieldKey}-cc`}
						placeholder='name@company.com'
						ariaLabel='Cc recipients'
						value={ccValue}
						onChange={setCcValue}
					/>
					{showAddCsm && csm && (
						<Button
							variant='ghost'
							size='sm'
							styles={[styles.addressToggleBtn]}
							onClick={() => setCcValue(prev => (prev.trim() ? `${prev}, ${csm}` : csm))}
						>
							{csmLabel}
						</Button>
					)}
				</div>
			)}
			{showBcc && (
				<div className={css(styles.addressSubRow)}>
					<span className={css(styles.addressLabel)}>Bcc</span>
					<ComposerRecipientInput
						{...suggestionProps}
						key={`${fieldKey}-bcc`}
						placeholder='name@company.com'
						ariaLabel='Bcc recipients'
						value={bccValue}
						onChange={setBccValue}
					/>
				</div>
			)}
		</div>
	);
};
